<template>
  <div
    ref="selectRef"
    :class="selectClasses"
    class="relative transition-all duration-300 ease-in-out text-gray-600"
  >
    <div
      :class="paddingClasses"
      class="cursor-pointer text-left"
      @click="toggleOpen"
    >
      <div class="relative pl-2 flex">
        <slot name="selectedOptionPrefix"></slot>
        <span v-if="!internalSelectedOption">{{ placeholder }}</span>
        <slot v-else name="label" v-bind="internalSelectedOption">{{
          internalSelectedOption.label
        }}</slot>
        <div
          v-show="transformedOptions.length > 0"
          class="absolute right-2 top-0.5 bottom-0"
        >
          <IconChevronDown
            class="w-[20px] text-gray-400 transition-all duration-300 ease-in-out"
            :class="{ 'rotate-180': isOpen }"
          />
        </div>
      </div>
    </div>
    <div
      :class="{
        hidden: !isOpen,
        'border absolute w-full bg-white left-[-1px] rounded-b box-content':
          positionAbsolute,
      }"
    >
      <div ref="optionsListRef" class="px-2 max-h-[250px] overflow-y-scroll">
        <div
          v-for="(option, idx) in transformedOptions"
          :key="option.id"
          :class="optionClasses(option, idx)"
          :data-test-id="`select-option-${option.id}`"
          @click="onSelectOption(option)"
        >
          <slot name="option" v-bind="option">{{ option.label }}</slot>
        </div>
        <div
          v-if="enableDeselectOption"
          :class="paddingClasses"
          class="transition-all duration-100 ease cursor-pointer"
          @click="onSelectOption(undefined)"
        >
          {{ $t('multiselect.deselectLabel') }}
        </div>

        <div v-show="loadingMoreOptions" class="w-full animate-pulse">
          <hr class="mb-1" />
          <div class="h-[35px] bg-gray-200 rounded mb-2"></div>
          <div class="h-[35px] bg-gray-200 rounded mb-2"></div>
          <span class="sr-only">Loading...</span>
        </div>
        <slot name="endOfList"></slot>
      </div>
      <slot name="afterList"></slot>
    </div>
  </div>
</template>

<script
  lang="ts"
  setup
  generic="T extends SelectOptionPropValue | string | number"
>
export interface SelectOptionPropValue {
  [key: string]: any
}

export interface SelectOptionProp<T> {
  label: string
  disabled?: boolean
  value: T | undefined
  id: string | number
}

export interface SelectTransformedOption<T>
  extends Partial<SelectOptionProp<T>> {
  id: string | number
}

export interface SelectInternalOption<T> extends SelectTransformedOption<T> {
  originalIndex: number
}

const props = withDefaults(
  defineProps<{
    placeholder?: string
    options: T[] | null
    size?: 'small' | 'default' | 'large'
    loadingMoreOptions?: boolean
    positionAbsolute?: boolean
    enableDeselectOption?: boolean
    transformerFn?: (option: T, idx: number) => SelectTransformedOption<T>
  }>(),
  {
    placeholder: 'Choose an option',
    size: 'default',
    loadingMoreOptions: false,
    positionAbsolute: false,
    enableDeselectOption: false,
    transformerFn: (option: T, idx) => ({
      id: idx,
      label: option as unknown as string,
      value: option,
    }),
  }
)

const emit = defineEmits(['infinite-scroll-callback'])
const selectRef = ref<HTMLElement | null>(null)
const optionsListRef = ref<HTMLElement | null>(null)

if (process.client) {
  onClickOutside(selectRef, () => {
    isOpen.value = false
  })
}

const selectedOption = defineModel<T | undefined>(undefined)
const internalSelectedOption = ref<SelectInternalOption<T> | undefined>(
  undefined
)
const isOpen = ref(false)

const onSelectOption = (option: SelectInternalOption<T>) => {
  if (option?.disabled) {
    return
  }

  selectedOption.value = props.options?.[option?.originalIndex] || undefined
  if (option === undefined) internalSelectedOption.value = undefined

  isOpen.value = false
}

const toggleOpen = () => {
  isOpen.value = !isOpen.value
}

useInfiniteScroll(
  optionsListRef,
  async () => {
    emit('infinite-scroll-callback')
    await nextTick()
  },
  {
    distance: 40,
    // check if list is visible
    canLoadMore: () => !props.loadingMoreOptions,
  }
)

const internalOptionTransformerFn = (
  option: T,
  idx: number
): SelectInternalOption<T> => {
  const transformedOption = props.transformerFn(option, idx)
  return {
    ...transformedOption,
    originalIndex: idx,
  }
}

const transformedOptions = computed(() => {
  return (
    props.options
      ?.map(internalOptionTransformerFn)
      .filter((option) => option.id !== internalSelectedOption.value?.id) || []
  )
})

watchEffect(() => {
  if (selectedOption.value) {
    internalSelectedOption.value = internalOptionTransformerFn(
      selectedOption.value,
      props.options?.indexOf(selectedOption.value) || 0
    )
  }
})

const selectClasses = computed(() => ({
  block: true,
  'w-full': true,
  'text-sm': props.size === 'small' || props.size === 'default',
  'text-base': props.size === 'large',
  'text-gray-900': true,
  border: true,
  'border-gray-300': true,
  'rounded-t': true,
  'rounded-b':
    !props.positionAbsolute || (!isOpen.value && props.positionAbsolute),
  'focus:ring-blue-500': true,
  'focus:border-blue-500': true,
}))

const paddingClasses = computed(() => ({
  'px-1 py-1.5': props.size === 'small',
  'px-1 py-3': props.size === 'default',
  'px-2 py-4': props.size === 'large',
}))

const optionClasses = (option: SelectInternalOption<T>, idx: number) => [
  {
    'bg-gray-100 text-gray-400 rounded': option.disabled,
    'cursor-pointer text-gray-600': !option.disabled,
    'border-t-0': idx === 0 && props.positionAbsolute,
    'hover:text-gray-700 hover:font-semibold':
      !option.disabled && option.id !== internalSelectedOption.value?.id,
    'cursor-default':
      option.disabled || option.id === internalSelectedOption.value?.id,
    'border-b':
      !option.disabled && !transformedOptions.value[idx + 1]?.disabled,
  },
  paddingClasses.value,
  'transition-all duration-100 ease mt-[1px] first:mt-0 text-left',
]
</script>
